import { Plus } from 'lucide-react';
import { Button } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import type { SearchDatasetDto, SearchFilterGroup, SearchGroupLogic } from '@/shared/api';
import { defaultRuleFor } from './filterModel';
import { FilterRuleRow } from './FilterRuleRow';

interface Props {
  dataset: SearchDatasetDto;
  group: SearchFilterGroup;
  onChange: (group: SearchFilterGroup) => void;
}

export function FilterBuilder({ dataset, group, onChange }: Props) {
  const setLogic = (logic: SearchGroupLogic) => onChange({ ...group, logic });
  const addRule = () =>
    onChange({ ...group, rules: [...group.rules, defaultRuleFor(dataset.fields[0]!)] });
  const updateRule = (index: number, rule: (typeof group.rules)[number]) =>
    onChange({ ...group, rules: group.rules.map((r, i) => (i === index ? rule : r)) });
  const removeRule = (index: number) =>
    onChange({ ...group, rules: group.rules.filter((_, i) => i !== index) });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <span className="text-ink-soft text-[13px] font-medium">Treffer erfüllen</span>
        <div className="border-line/60 inline-flex overflow-hidden rounded-md border">
          {(['And', 'Or'] as const).map((logic) => (
            <button
              key={logic}
              type="button"
              onClick={() => setLogic(logic)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium transition-colors',
                group.logic === logic ? 'bg-navy text-white' : 'text-ink-soft hover:bg-card-alt',
              )}
            >
              {logic === 'And' ? 'ALLE' : 'MIND. EINE'}
            </button>
          ))}
        </div>
        <span className="text-ink-faint text-xs">der folgenden Bedingungen</span>
      </div>

      {group.rules.length === 0 ? (
        <p className="text-ink-faint text-sm">
          Keine Bedingungen — liefert den gesamten erlaubten Bestand.
        </p>
      ) : (
        <div className="space-y-2">
          {group.rules.map((rule, index) => (
            <FilterRuleRow
              key={index}
              rule={rule}
              fields={dataset.fields}
              onChange={(next) => updateRule(index, next)}
              onRemove={() => removeRule(index)}
            />
          ))}
        </div>
      )}

      <Button
        variant="ghost"
        size="sm"
        leftIcon={<Plus size={15} strokeWidth={2} />}
        onClick={addRule}
      >
        Bedingung hinzufügen
      </Button>
    </div>
  );
}
